import { Flex, Popconfirm, Tag, Tooltip } from 'antd';
import { AuthorizedIcon, Icon } from '../../../components';
import TrashSolidBlack from '../../../assets/icons/trash-solid-b-black.svg';
import PenToSquareSolidBlack from '../../../assets/icons/pen-to-square-solid-w-black.svg';
import EyeRegularWBlack from '../../../assets/icons/eye-regular-w-black.svg';
import Variant99Black from '../../../assets/icons/variant99-black.svg';
import FolderOpenRegular from '../../../assets/icons/folder-open-regular.svg';
import Addendum from '../../../assets/icons/addendum.svg';
import styles from './index.module.scss';
import { status_colors } from '../../../assets/conts';
import { IEnum } from '../../../services/type';
import { getValueFromEnum } from '../../../utils/functions';
import { ColumnType } from 'antd/es/table';
import { actionTypes } from '../../../assets/enums';
import {
  IFile,
  IOrganization,
  IDomesticInsuranceDataSource,
} from '../../../types';

export const getColumns = (
  handleClickEye: (id: number) => void,
  handleOpenEditModal: (id: number) => void,
  handleClickDelete: (id: number) => Promise<void>,
  handleClickCancel: (id: number) => Promise<void>,
  handleClickAddendum: (id: number) => Promise<void>,
  handleSetAdditionalFile: (id: number, file: IFile[]) => void,
  organizations: IOrganization[],
  types: IEnum[],
  branches: IEnum[],
  statusChoices: IEnum[],
): Array<
  ColumnType<IDomesticInsuranceDataSource> & { searchable?: boolean }
> => [
  {
    title: 'Şirket',
    dataIndex: 'organization',
    key: 'organization',
    width: 125,
    searchable: true,
    filters: organizations.map((item) => ({
      text: item.name || '',
      value: item.id,
    })),
    sorter: (a, b) => a.organization - b.organization,
    render: (_, record) => (
      <Tooltip
        title={
          organizations.find((item) => item.id === record.organization)?.name
        }
      >
        <span>{`${organizations.find((item) => item.id === record.organization)?.name.slice(0, 15)}...`}</span>
      </Tooltip>
    ),
  },
  {
    title: 'Poliçe No',
    dataIndex: 'policy_number',
    key: 'policy_number',
    width: 150,
    searchable: true,
    sorter: (a, b) => a.policy_number - b.policy_number,
  },
  {
    title: 'Poliçe Tipi',
    dataIndex: 'type',
    key: 'type',
    width: 135,
    filters: types.map((item) => ({
      text: getValueFromEnum(types, item.key) || '',
      value: item.key,
    })),
    filterMode: 'tree',
    filterSearch: true,
    sorter: (a, b) => a.type?.localeCompare(b.type),
    render: (_, record) => (
      <Tag className={styles[`${record.type}Tag`]}>
        {getValueFromEnum(types, record.type)}
      </Tag>
    ),
  },
  {
    title: 'Branş',
    dataIndex: 'branch',
    key: 'branch',
    width: 150,
    filters: branches.map((item) => ({
      text: getValueFromEnum(branches, item.key) || '',
      value: item.key,
    })),
    filterMode: 'tree',
    filterSearch: true,
    sorter: (a, b) => a.branch?.localeCompare(b.branch),
    render: (_: unknown, record: IDomesticInsuranceDataSource) => (
      <div>{getValueFromEnum(branches, record.branch)}</div>
    ),
  },
  {
    title: 'Başlangıç Tarihi',
    dataIndex: 'start_date',
    key: 'start_date',
    width: 150,
    sorter: true,
  },
  {
    title: 'Bitiş Tarihi',
    dataIndex: 'end_date',
    key: 'end_date',
    width: 120,
    sorter: true,
  },
  {
    title: 'Açıklama',
    dataIndex: 'description',
    key: 'description',
    width: 200,
    sorter: (a, b) => a.description?.localeCompare(b.description),
    render: (_, record) => (
      <Tooltip title={record.description}>
        <span>
          {record.description?.length
            ? `${record.description?.slice(0, 15)}...`
            : ''}
        </span>
      </Tooltip>
    ),
  },
  {
    title: 'Durum',
    dataIndex: 'recent_status',
    key: 'recent_status',
    width: 150,
    filters: statusChoices.map((item) => ({
      text: getValueFromEnum(statusChoices, item.key) || '',
      value: item.key,
    })),
    filterMode: 'tree',
    filterSearch: true,
    render: (_: unknown, record: IDomesticInsuranceDataSource) => (
      <Tag color={status_colors[record.recent_status]}>
        {getValueFromEnum(statusChoices, record.recent_status)}
      </Tag>
    ),
  },
  {
    title: 'İşlem',
    dataIndex: 'action',
    key: 'action',
    width: 160,
    render: (_: unknown, record: IDomesticInsuranceDataSource) => (
      <Flex justify="flex-end" gap={14}>
        {record.recent_status !== 'endorsement_request' && (
          <AuthorizedIcon
            action="add"
            model="motorinsuranceendorsementrequest"
            source={Addendum}
            popoverText={actionTypes.ADDENDUM}
            onClick={() => record?.id && handleClickAddendum(record.id)}
          />
        )}
        <Icon
          source={EyeRegularWBlack}
          popoverText={actionTypes.VIEW}
          onClick={() => record?.id && handleClickEye(record.id)}
        />
        {record.recent_status !== 'cancelled' && (
          <Popconfirm
            title={
              <div className={styles.popconfirmTitle}>
                İptal işlemini onaylıyor musunuz?
              </div>
            }
            overlayInnerStyle={{
              width: 150,
              borderColor: '#D6222A',
              borderWidth: 1,
              borderStyle: 'solid',
            }}
            okText="Onayla"
            cancelText="İptal"
            icon={null}
            color="#FAFAFA"
            okButtonProps={{ danger: true, style: { width: '45%' } }}
            cancelButtonProps={{ danger: true, style: { width: '45%' } }}
            onConfirm={() => record.id && handleClickCancel(record.id)}
          >
            <AuthorizedIcon
              action="add"
              model="domesticinsurance"
              popoverText={actionTypes.CANCEL}
              source={Variant99Black}
            />
          </Popconfirm>
        )}
        <Popconfirm
          title={
            <div className={styles.popconfirmTitle}>
              Silme işlemini onaylıyor musunuz?
            </div>
          }
          overlayInnerStyle={{
            width: 150,
            borderColor: '#D6222A',
            borderWidth: 1,
            borderStyle: 'solid',
          }}
          okText="Onayla"
          cancelText="İptal"
          icon={null}
          color="#FAFAFA"
          okButtonProps={{ danger: true, style: { width: '45%' } }}
          cancelButtonProps={{ danger: true, style: { width: '45%' } }}
          onConfirm={() => record.id && handleClickDelete(record.id)}
        >
          <AuthorizedIcon
            action="delete"
            model="domesticinsurance"
            popoverText={actionTypes.DELETE}
            source={TrashSolidBlack}
          />
        </Popconfirm>
        <AuthorizedIcon
          action="change"
          model="domesticinsurance"
          popoverText={actionTypes.CHANGE}
          source={PenToSquareSolidBlack}
          onClick={() => record?.id && handleOpenEditModal(record.id)}
        />
        <AuthorizedIcon
          action="view"
          model="domesticinsuranceadditionalfile"
          popoverText={actionTypes.ADDITIONAL_FILE}
          source={FolderOpenRegular}
          onClick={() =>
            handleSetAdditionalFile(record.id, record.additional_files)
          }
        />
      </Flex>
    ),
  },
];
